import { useState } from 'react'
import type { EventState } from '../../event/types'

type Key = 'name' | 'color' | 'members' | 'mission' | 'clientDomain' | 'technology'

const COLUMNS: { key: Key; label: string }[] = [
  { key: 'name', label: 'Team' },
  { key: 'color', label: 'Color' },
  { key: 'members', label: 'Members' },
  { key: 'mission', label: 'Mission' },
  { key: 'clientDomain', label: 'Domain' },
  { key: 'technology', label: 'Technology' },
]

/**
 * Every team revealed so far, with its color and its assignment. Teams the
 * wheel has not reached stay out of the table, so it is safe on the projector.
 */
export default function TeamsTable({ state }: { state: EventState }) {
  const [sort, setSort] = useState<{ key: Key; dir: 'asc' | 'desc' }>({ key: 'name', dir: 'asc' })

  const byId = new Map(state.roster.map((p) => [p.id, p]))
  const rows = state.teams.slice(0, state.revealedCount).map((team) => {
    const mission = state.missions.find((m) => m.id === state.assignments[team.id])
    return {
      id: team.id,
      name: team.name,
      color: team.color,
      members: team.memberIds.map((id) => byId.get(id)?.name ?? id).join(' + '),
      mission: mission?.mission ?? '',
      clientDomain: mission?.clientDomain ?? '',
      technology: mission?.technology ?? '',
    }
  })
  if (rows.length === 0) return null

  const sign = sort.dir === 'asc' ? 1 : -1
  const sorted = [...rows].sort((a, b) => sign * a[sort.key].localeCompare(b[sort.key]) || a.name.localeCompare(b.name))

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[48rem] text-left text-sm">
        <caption className="mb-2 text-left text-xl font-black tracking-tight">Teams</caption>
        <thead>
          <tr className="border-b border-[var(--page-rule)]">
            {COLUMNS.map((column) => (
              <th
                key={column.key}
                scope="col"
                className="py-2 pr-3"
                aria-sort={sort.key === column.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
              >
                <button
                  type="button"
                  className="inline-flex cursor-pointer items-center gap-1 font-bold hover:underline"
                  onClick={() =>
                    setSort((s) => (s.key === column.key ? { key: s.key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key: column.key, dir: 'asc' }))
                  }
                >
                  {column.label}
                  <span aria-hidden="true" className="text-xs">
                    {sort.key === column.key ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
                  </span>
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => (
            <tr key={row.id} className="border-b border-[var(--page-rule)]">
              <td className="py-2 pr-3 font-bold">{row.name}</td>
              <td className="py-2 pr-3">
                <span className="inline-flex items-center gap-2 font-mono">
                  <span aria-hidden="true" className="inline-block size-5 rounded border border-[var(--page-rule)]" style={{ background: row.color }} />
                  {row.color}
                </span>
              </td>
              <td className="py-2 pr-3 font-semibold">{row.members}</td>
              <td className="py-2 pr-3">{row.mission}</td>
              <td className="py-2 pr-3">{row.clientDomain}</td>
              <td className="py-2 pr-3">{row.technology}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
